<template>
  <div id="whale-backtester-signals-tab">
    <helpers-section-header v-show="!isSignalView" class="mt-0 mb-2">
      Choose Signal Sources
    </helpers-section-header>
    <p v-show="!isSignalView" class="text-body-2 mb-4">
      Signals are the buy triggers for your backtester. Enable one or more signal types and
      configure each one using the <strong>Configure</strong> button. You can combine signals
      using <strong>AND</strong>, <strong>OR</strong>, and <strong>Filter</strong> logic — see
      the grouping explanation below for how they interact.
    </p>

    <!-- Signals -->
    <v-window
      v-if="whaleTraderCriteria && whaleTraderCriteria.signals"
      v-model="isSignalView"
      touchless
    >
      <!-- Signals: List -->
      <v-window-item :value="0">
        <v-expansion-panels
          v-model="selectedSignalsIndexes"
          accordion
          multiple
          readonly
          class="d-inline-block"
          style="width: auto"
        >
          <v-expansion-panel
            v-for="signal in Object.values(whaleTraderCriteria.signals).filter(s => s)"
            :key="signal.signal_id"
            class="mb-2"
          >
            <v-expansion-panel-title hide-actions style="cursor: default">
              <v-row no-gutters align="center">
                <v-col>
                  <v-switch v-model="signal.enabled" color="primary" hide-details class="mt-0" />
                </v-col>
                <v-col cols="10">
                  <h3 class="text-h5 mb-0">{{ signal.name }} Signals</h3>
                  <div class="text-justify text-body-2">
                    {{ signal.description }}
                  </div>
                </v-col>
              </v-row>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-btn
                :disabled="!signal.enabled"
                class="d-flex justify-space-between"
                theme="dark"
                color="primary"
                variant="outlined"
                @click="setCurrentSignalView(signal.signal_id)"
              >
                Configure
              </v-btn>
              <v-radio-group v-show="signal.enabled" v-model="signal.join_type" row hide-details>
                <v-radio label="AND — must be present alongside all other AND signals" value="and" color="primary" hint="" persistent-hint />
                <v-radio label="OR — triggers independently" value="or" color="primary" hint="" persistent-hint />
                <v-radio
                  v-if="signal.signal_id == 4 || signal.signal_id == 7 || signal.signal_id == 8"
                  label="Filter — narrows which stocks other signals can pick"
                  value="filter"
                  color="primary"
                  hint=""
                  persistent-hint
                />
              </v-radio-group>
              <v-dialog v-model="stock_basket_dialog" persistent>
                <template #activator="{ props }">
                  <v-btn
                    v-if="signal.signal_id === 4"
                    :disabled="!signal.enabled || signal.join_type != 'filter'"
                    color="primary"
                    class="mt-4"
                    v-bind="props"
                  >
                    Add Optional Stock Basket
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title class="text-h5">
                    Create a basket of stocks that will always be evaluated.
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <!-- Optionally restrict to specific securities -->
                      <v-col cols="12" mb-6>
                        <helpers-section-sub-header class="mb-6">
                          These stocks will always be evaluated for your trends even if they don't
                          appear as a signal. Use this to force your model to use a basket of fixed
                          income ETFs for example as long as they show some sign of trending.
                        </helpers-section-sub-header>

                        <core-stock-manager
                          :key="'sm-' + current_trading_model.id"
                          v-model="signal.stock_basket"
                          flat
                          no-padding
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn color="primary" text @click="stock_basket_dialog = false"> Save </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-window-item>

      <!-- Signals: Current View -->
      <v-window-item :value="1">
        <v-btn v-show="isSignalView" text color="primary" @click="backToAllSignals()">
          <v-icon left>
            {{ mdiKeyboardBackspace }}
          </v-icon>
          Back to All Signals
        </v-btn>
        <v-divider class="my-4" />
        <component :is="currentSignalComponent" />
        <v-divider class="my-4" />
        <v-btn v-show="isSignalView" text color="primary" @click="backToAllSignals()">
          <v-icon left>
            {{ mdiKeyboardBackspace }}
          </v-icon>
          Back to All Signals
        </v-btn>
      </v-window-item>
    </v-window>

    <template v-if="!isSignalView">
      <!-- Info note: Signal grouping -->
      <helpers-section-header>Signal Grouping: AND / OR / Filter</helpers-section-header>
      <p class="text-body-2 mb-2">
        When you enable multiple signal types, you control how they combine to determine which
        stocks are selected as buy candidates:
      </p>
      <ul class="text-body-2 mb-2 pl-4">
        <li class="mb-1">
          <strong>AND</strong> — All enabled AND signals must fire simultaneously for a stock to be
          selected. The stock must pass every AND signal at once.
        </li>
        <li class="mb-1">
          <strong>OR</strong> — Any single OR signal firing is enough. A stock matching any one OR
          signal is selected (subject to AND requirements also being met).
        </li>
        <li class="mb-1">
          <strong>Filter</strong> — Available for Trend, Fundamental, and 13F Indicator signals.
          When set to Filter, this signal does not generate buy candidates on its own — instead it
          gates the stock list produced by your AND/OR signals, removing any stock that doesn't
          also meet the filter criteria.
        </li>
      </ul>
      <p class="text-body-2 mb-3">
        <strong>Example:</strong> 13F signal set to OR, Insider signal set to OR, Trend signal set
        to AND. Result: a stock is selected if it appears in either the 13F or Insider signal
        <em>and</em> also passes the Trend rules. The Trend signal alone will never generate a buy.
      </p>

      <!-- Info note: Signal Sorting and Prioritizing -->
      <helpers-section-header>Signal Sorting and Priority</helpers-section-header>
      <p class="text-body-2 mb-2">
        When multiple stocks qualify on the same day and the portfolio is near capacity, the
        backtester uses this ordering to decide which stocks to buy first. Signal-specific ordering
        uses each signal type's own internal ranking (e.g., 13F ranking score). The 1-month
        performance options buy recent winners (momentum) or recent laggards (mean-reversion) first.
      </p>
      <v-row>
        <v-col cols="12">
          <v-radio-group
            v-model="whaleTraderCriteria.general.signal_ordering"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="''" label="Signal-specific ordering" color="primary" />
            <v-radio :value="'high'" label="1-month performance: high to low (momentum)" color="primary" />
            <v-radio :value="'low'" label="1-month performance: low to high (contrarian)" color="primary" />
          </v-radio-group>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
// Modules

import { useGoTo } from 'vuetify'
import { mapWritableState } from 'pinia'
import { useAppStore } from '@/store'
import { markRaw } from 'vue'
// Components
import F13Signal from '@/components/page/whale-backtester/signals/_F13Signal'
import D13Signal from '@/components/page/whale-backtester/signals/_D13Signal'
import InsiderSignal from '@/components/page/whale-backtester/signals/_InsiderSignal'
import TrendSignal from '@/components/page/whale-backtester/signals/_TrendSignal2'
import WhaleMLSignal from '@/components/page/whale-backtester/signals/_WhaleMLSignal'
import WhaleIndexSignal from '@/components/page/whale-backtester/signals/_WhaleIndexSignal'
import FundamentalSignal from '@/components/page/whale-backtester/signals/_FundamentalSignal'
import F13IndicatorSignal from '@/components/page/whale-backtester/signals/_F13IndicatorSignal'
import WatchlistSignal from '@/components/page/whale-backtester/signals/_WatchlistSignal'
import { mdiKeyboardBackspace } from '@mdi/js'

export default {
  components: {
    F13Signal: markRaw(F13Signal),
    D13Signal: markRaw(D13Signal),
    InsiderSignal: markRaw(InsiderSignal),
    TrendSignal: markRaw(TrendSignal),
    WhaleMLSignal: markRaw(WhaleMLSignal),
    WhaleIndexSignal: markRaw(WhaleIndexSignal),
    FundamentalSignal: markRaw(FundamentalSignal),
    F13IndicatorSignal: markRaw(F13IndicatorSignal),
    WatchlistSignal: markRaw(WatchlistSignal)
  },

  setup() {
    const goTo = useGoTo()
    return { goTo }
  },

  data() {
    return {
      mdiKeyboardBackspace,
      isSignalView: 0,
      currentSignalView: null,
      stock_basket_dialog: false
    }
  },
  computed: {
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'current_trading_model',
      'signalView',
      'app.buy_signal_frequencies'
    ]),
    ...mapWritableState(useAppStore, { criteria: 'whaleTraderCriteria' }),
    // ...mapWritableState(useUserStore, ["tradingModels", "sharedModels","trading_model_update",
    // "status_update_snackbar",'user', 'tradingModels']),
    /*...mapFields({
      whaleTraderCriteria: 'app.whaleTraderCriteria',
      current_trading_model: 'app.current_trading_model',
      // signalView: 'app.signalView',
      buy_signal_frequencies: 'app.buy_signal_frequencies'*/
    //}),
    selectedSignalsIndexes() {
      const signals = Object.values(this.whaleTraderCriteria.signals)

      return signals.filter(signal => signal?.enabled).map(signal => signals.indexOf(signal))
    },
    currentSignalComponent() {
      const mapping = {
        1: 'F13Signal',
        2: 'D13Signal',
        3: 'InsiderSignal',
        4: 'TrendSignal',
        5: 'WhaleMLSignal',
        6: 'WhaleIndexSignal',
        7: 'FundamentalSignal',
        8: 'F13IndicatorSignal',
        9: 'WatchlistSignal'
      }

      return mapping[this.currentSignalView]
    }
  },
  methods: {
    setCurrentSignalView(id) {
      this.isSignalView = 1
      this.currentSignalView = id
      setTimeout(() => this.goTo('#whale-backtester-page-current-model', { offset: 15 }), 400)
    },
    backToAllSignals() {
      this.isSignalView = 0
      setTimeout(() => this.goTo('#whale-backtester-page-current-model', { offset: 15 }), 400)
    }
  }
}
</script>
